"use strict";
/**
 * Requests for creative (RFCs) on NewForm One: the spec a creator recreates
 * an ad from, the brief they read, and the window and guarantee staff set.
 * Shared by the backend service and the frontend forms so both sides agree on
 * limits and defaults.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CONTENT_REQUEST_CREATOR_BRIEF_LIMITS = exports.CONTENT_REQUEST_SPEC_EXAMPLE_ROLES = exports.CONTENT_REQUEST_SPEC_EXAMPLE_KINDS = exports.CONTENT_REQUEST_SPEC_CORE_KEYS = exports.CONTENT_REQUEST_SPEC_SOURCES = exports.CONTENT_REQUEST_SPEC_PARAM_LABELS = exports.CONTENT_REQUEST_SPEC_PARAM_KEYS = exports.CONTENT_REQUEST_MAX_USD = exports.CONTENT_REQUEST_MAX_WINDOW_HOURS = exports.CONTENT_REQUEST_DEFAULT_GUARANTEE_USD = exports.PAYOUT_REQUEST_MINIMUM_USD = exports.CONTENT_REQUEST_DEFAULT_WINDOW_HOURS = void 0;
exports.roundCents = roundCents;
exports.renderCreatorBrief = renderCreatorBrief;
/** How long a request stays open once published unless staff say otherwise. */
exports.CONTENT_REQUEST_DEFAULT_WINDOW_HOURS = 72;
/**
 * The least a request can guarantee, so what every video submitted while a
 * request is open earns at least: this or its share of ad spend, whichever is
 * higher. Creator agreement v4 section 2(a) promises exactly that, so lowering
 * it needs a new agreement version creators sign, and the
 * creator_content_request_guarantee_usd_check constraint (migration
 * 20261005180000_creator_content_request_guarantee) moves with it.
 */
exports.PAYOUT_REQUEST_MINIMUM_USD = 100;
/** The guarantee a new request proposes; staff can raise it. */
exports.CONTENT_REQUEST_DEFAULT_GUARANTEE_USD = exports.PAYOUT_REQUEST_MINIMUM_USD;
/** Sixty days: longer than any real request, short enough that a forgotten one expires. */
exports.CONTENT_REQUEST_MAX_WINDOW_HOURS = 60 * 24;
/** Ceiling for a request's guarantee. */
exports.CONTENT_REQUEST_MAX_USD = 10000;
function roundCents(value) {
    // Work in cents and nudge by a millionth of a cent, so a value float
    // arithmetic left a hair under an x.xx5 boundary (1234.565 is stored as
    // 1234.56499…) rounds to the intended cent at any dollar magnitude the
    // schema allows. A millionth of a cent can never flip a legitimate value.
    return Math.round(value * 100 + 1e-6) / 100;
}
/**
 * The request spec: the parameters a creator needs to recreate an ad without
 * seeing it. Fixed keys, fixed order, every one present on every request;
 * "none" is a valid instruction ("no music", "no one on camera"). Grounded in
 * the 494 Framework winners of the 90 days to 2026-09-24: every one needed all
 * twelve and none needed a twelfth.
 */
exports.CONTENT_REQUEST_SPEC_PARAM_KEYS = [
    "hook",
    "concept",
    "beats",
    "format",
    "camera",
    "setting",
    "talent",
    "audio",
    "text",
    "product",
    "language",
    "cta",
];
/** Creator-facing row titles and what each row must answer. */
exports.CONTENT_REQUEST_SPEC_PARAM_LABELS = {
    hook: {
        label: "Hook",
        answers: "The first three seconds: the words and what is on screen.",
    },
    concept: {
        label: "Concept",
        answers: "The idea, the angle and the feeling it should trigger.",
    },
    beats: {
        label: "Beats",
        answers: "The beats in order with rough timings, and the total length.",
    },
    format: {
        label: "Format",
        answers: "The kind of video: green-screen talking head, split screen, POV, skit, montage.",
    },
    camera: {
        label: "Camera",
        answers: "Lens, framing, movement and point of view: direct to camera, fisheye, handheld, fixed.",
    },
    setting: {
        label: "Setting",
        answers: "Where it is filmed, props, time of day.",
    },
    talent: {
        label: "Talent",
        answers: "Who is on camera: persona, look, energy, wardrobe. Or no one.",
    },
    audio: {
        label: "Audio",
        answers: "On-camera speech, voiceover, music, sound design.",
    },
    text: {
        label: "On-screen text",
        answers: "Caption style, overlays, motion graphics.",
    },
    product: {
        label: "Product moment",
        answers: "When and how the product appears.",
    },
    language: {
        label: "Language",
        answers: "Must-say phrases, control language, what not to say.",
    },
    cta: { label: "Call to action", answers: "The ask, and any offer." },
};
exports.CONTENT_REQUEST_SPEC_SOURCES = [
    "WINNER",
    "STAFF",
    "GENERATED",
];
/**
 * The five parameters a creator needs before anything else, and the ones a
 * manual request requires by default: the UI shows them first and expanded.
 * Ty's call, 2026-09-24, from the Will Bot RFC card (format, setting, talent,
 * camera, text overlay).
 */
exports.CONTENT_REQUEST_SPEC_CORE_KEYS = [
    "format",
    "camera",
    "setting",
    "talent",
    "text",
];
exports.CONTENT_REQUEST_SPEC_EXAMPLE_KINDS = ["AD", "SUBMISSION"];
exports.CONTENT_REQUEST_SPEC_EXAMPLE_ROLES = [
    "WINNER",
    "REFERENCE",
];
/**
 * Word limits of the creator-facing brief: the Format Scout pack the team
 * accepted on 2026-09-24 (name, one line, three shoot bullets, one short
 * script, one footer of rules). The draft service enforces them with a
 * rewrite pass; the schema only bounds characters.
 */
exports.CONTENT_REQUEST_CREATOR_BRIEF_LIMITS = {
    nameWords: 5,
    oneLinerWords: 20,
    bullets: 3,
    bulletWords: 22,
    scriptWords: 60,
    wordingNoteWords: 15,
    footerMinRules: 3,
    footerMaxRules: 6,
    footerRuleWords: 25,
};
/**
 * The brief text a creator reads, rendered from the creator brief so the
 * stored `brief` column and the structured spec never disagree. Plain text,
 * no markup: it is shown as is in the portal, the app, emails and Slack. The
 * spec rows are deliberately not part of it; they are the detail view.
 */
function renderCreatorBrief(brief) {
    var _a;
    const lines = [
        brief.name.trim(),
        brief.oneLiner.trim(),
        "",
        "Shoot",
        ...brief.bullets.map((bullet, index) => `${index + 1}. ${bullet.trim()}`),
        "",
        "Script",
        brief.script.trim(),
    ];
    if ((_a = brief.wordingNote) === null || _a === void 0 ? void 0 : _a.trim()) {
        lines.push("", `Wording: ${brief.wordingNote.trim()}`);
    }
    if (brief.footer.length > 0) {
        lines.push("", "Every video", ...brief.footer.map((rule) => `- ${rule.trim()}`));
    }
    return lines.join("\n");
}
